import type { Event, Invoice, RSVP } from '@/types';
import { computeCreditRecoveryTotalPounds } from '@/lib/credit-booking-fee';
import { getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';
import {
  computeInvoiceBaseAmountForPayer,
  computeRsvpSubtotalForInvoice,
  computeRsvpTicketSubtotalCurrentPrices,
  countHostInvoiceCreditRecoverySlots,
  getCanonicalInvoiceRsvpIds,
  isSelfPayingInvitee,
} from '@/lib/ensure-invoice-for-rsvp';
import { getInvoicePayerRsvp } from '@/lib/invoice-payer-rsvp';
import { getInvoiceTicketAmountDue, getAmountReservedOnInvoice } from '@/lib/invoice-utils';

/**
 * True when this invoice line should contribute to credit/booking recovery (host-owed seat with a priced ticket).
 * Zero-priced seats omit recovery here; that fee is paid on the guest's own checkout when they pay separately.
 */
export function isBookingFeeBillableInvoiceRow(
  rsvp: RSVP,
  event: Event,
  invoiceLineCount: number,
): boolean {
  if (!rsvp || rsvp.apologies) return false;
  if (isSelfPayingInvitee(rsvp) && invoiceLineCount > 1) {
    return false;
  }
  return computeRsvpSubtotalForInvoice(rsvp, event) > 0;
}

export function countBookingFeeBillableSlots(
  orderedRsvps: RSVP[],
  event: Event,
  invoiceLineCount: number,
): number {
  return orderedRsvps.filter((r) => isBookingFeeBillableInvoiceRow(r, event, invoiceLineCount)).length;
}

export function sumBookingFeeBillableTicketSubtotal(
  orderedRsvps: RSVP[],
  event: Event,
  invoiceLineCount: number,
): number {
  return orderedRsvps
    .filter((r) => isBookingFeeBillableInvoiceRow(r, event, invoiceLineCount))
    .reduce((s, r) => s + computeRsvpSubtotalForInvoice(r, event), 0);
}

/** Display name for a row on the public payment breakdown. */
export function formatRsvpGuestName(rsvp: RSVP): string {
  const first = typeof rsvp.firstName === 'string' ? rsvp.firstName.trim() : '';
  const last = typeof rsvp.lastName === 'string' ? rsvp.lastName.trim() : '';
  if (first || last) {
    return [first, last].filter(Boolean).join(' ');
  }
  const legacy = typeof rsvp.name === 'string' ? rsvp.name.trim() : '';
  return legacy || 'Guest';
}

/**
 * Human-readable ticket selection (matches invoice subtotal logic where possible).
 */
export function describeRsvpTicketSelection(rsvp: RSVP, event: Event): string {
  if (rsvp.customPrice !== undefined && rsvp.customPrice !== null && Number.isFinite(Number(rsvp.customPrice))) {
    return 'Custom pricing';
  }
  if (event.tickets?.length && Array.isArray(rsvp.responses?.Tickets)) {
    const parts: string[] = [];
    for (const line of rsvp.responses.Tickets as { id?: string; qty?: number }[]) {
      const qty = Number(line?.qty || 0);
      if (!qty || !line?.id) {
        continue;
      }
      const t = event.tickets.find((x) => x.id === line.id);
      const name = t?.name || 'Ticket';
      parts.push(`${name} ×${qty}`);
    }
    if (parts.length) {
      return parts.join(', ');
    }
  }
  if (event.tickets?.length) {
    return 'Ticket';
  }
  return 'Event ticket';
}

/** RSVPs in the same order as `invoice.rsvps` (only those successfully loaded). */
export function orderRsvpsForInvoice(invoice: Invoice, loaded: RSVP[]): RSVP[] {
  const byId = new Map(loaded.map((r) => [r.id, r]));
  return invoice.rsvps.map((id) => byId.get(id)).filter((r): r is RSVP => r != null);
}

/** Billable seats for an invoice payer (excludes stale ids still listed on `invoice.rsvps`). */
export function billableRsvpsForInvoicePayer(invoice: Invoice, allRsvps: RSVP[]): RSVP[] {
  const ordered = orderRsvpsForInvoice(invoice, allRsvps);
  if (ordered.length === 0) {
    return [];
  }
  const payer = getInvoicePayerRsvp(ordered[0], allRsvps);
  const byId = new Map(allRsvps.map((r) => [r.id, r]));
  return getCanonicalInvoiceRsvpIds(payer, allRsvps)
    .map((id) => byId.get(id))
    .filter((r): r is RSVP => r != null && !r.apologies);
}

/** Count of seats the payer covers (for display; ignores orphan ids on `invoice.rsvps`). */
export function countCanonicalInvoiceGuests(invoice: Invoice, allRsvps: RSVP[]): number {
  return billableRsvpsForInvoicePayer(invoice, allRsvps).length;
}

/**
 * Booking fee per billable seat (host-owed lines only on multi-line invoices).
 */
export function perSlotBookingFeeForBillableTotals(
  event: Event,
  billableTicketSubtotal: number,
  billableSlotCount: number,
  bookingFeePence: number,
): number {
  if (billableSlotCount <= 0) return 0;
  const total = computeCreditRecoveryTotalPounds(event, billableTicketSubtotal, billableSlotCount, bookingFeePence);
  if (total <= 0) return 0;
  return total / billableSlotCount;
}

/**
 * Ticket subtotal used to cap payable balance (invoice breakdown, else sum of loaded RSVP lines, else heuristic).
 */
export function ticketBaseForPayableAmount(invoice: Invoice, ticketSumFromLoadedRows: number): number {
  if (
    typeof invoice.allInPriceBreakdown?.ticketPrice === 'number' &&
    Number.isFinite(invoice.allInPriceBreakdown.ticketPrice) &&
    invoice.allInPriceBreakdown.ticketPrice >= 0
  ) {
    return invoice.allInPriceBreakdown.ticketPrice;
  }
  if (ticketSumFromLoadedRows > 0) {
    return ticketSumFromLoadedRows;
  }
  const storedRec = invoice.allInPriceBreakdown?.creditCostRecovery ?? 0;
  const o = Number(invoice.outstandingBalance) || 0;
  if (storedRec > 0 && o > storedRec) {
    return Math.max(0, o - storedRec);
  }
  return 0;
}

/**
 * Amount the guest may pay online today: never more than stored outstanding, and never more than
 * ticket subtotal plus booking fee under **current** event rules (drops stale recovery still stored on the invoice).
 * When `orderedRsvps` is provided, booking recovery uses only seats the invoice payer covers (excludes self-paying
 * invitees on a shared host invoice).
 */
export function computePayableOutstandingBalance(
  invoice: Invoice,
  event: Event | null | undefined,
  ticketSumFromLoadedRows: number,
  orderedRsvps?: RSVP[] | null,
): number {
  const raw = Number(invoice.outstandingBalance) || 0;
  if (!event) {
    return raw;
  }

  if (orderedRsvps && orderedRsvps.length > 0) {
    const billable = billableRsvpsForInvoicePayer(invoice, orderedRsvps);
    const billableSum = billable.reduce((s, r) => s + computeRsvpSubtotalForInvoice(r, event), 0);
    const billableCount = billable.filter((r) => computeRsvpSubtotalForInvoice(r, event) > 0).length;
    if (billableCount <= 0) {
      return raw;
    }
    const recovery = computeCreditRecoveryTotalPounds(
      event,
      billableSum,
      billableCount,
      getCreditBookingFeePenceForEvent(event),
    );
    const cap = billableSum + recovery;
    return Math.min(raw, cap);
  }

  const ticketBase = ticketBaseForPayableAmount(invoice, ticketSumFromLoadedRows);
  if (ticketBase <= 0) {
    return raw;
  }
  const n = invoice.rsvps?.length ?? 0;
  if (n <= 0) {
    return raw;
  }
  const recovery = computeCreditRecoveryTotalPounds(
    event,
    ticketBase,
    n,
    getCreditBookingFeePenceForEvent(event),
  );
  return Math.min(raw, ticketBase + recovery);
}

/**
 * Headline amount still owed on lists (personal dashboard, etc.): when event + RSVPs are loaded, uses
 * live billable ticket subtotal plus booking fee, minus {@link getAmountReservedOnInvoice} (confirmed
 * payments plus bank transfers awaiting approval). Does not cap by `outstandingBalance` for the live
 * total step. Without breakdown, falls back to {@link getInvoiceTicketAmountDue}.
 */
export function getHeadlinePayableDueForDashboard(
  invoice: Invoice,
  breakdown: { event: Event; rsvps: RSVP[] } | null | undefined,
): number {
  if (invoice.status === 'voided') {
    return 0;
  }
  if (!breakdown?.event || breakdown.rsvps.length === 0) {
    return getInvoiceTicketAmountDue(invoice);
  }
  const ordered = orderRsvpsForInvoice(invoice, breakdown.rsvps);
  if (ordered.length === 0) {
    return getInvoiceTicketAmountDue(invoice);
  }
  // Use canonical payer + guests only (stale ids on invoice.rsvps must not inflate totals).
  const billableRsvps = billableRsvpsForInvoicePayer(invoice, breakdown.rsvps);
  const billableEntries = billableRsvps.map((r) => ({
    subtotal: computeRsvpTicketSubtotalCurrentPrices(r, breakdown.event),
  }));
  const billableSum = billableEntries.reduce((s, e) => s + e.subtotal, 0);
  const billableCount = billableEntries.filter((e) => e.subtotal > 0).length;
  const feePence = getCreditBookingFeePenceForEvent(breakdown.event);
  const recovery =
    billableCount > 0
      ? computeCreditRecoveryTotalPounds(breakdown.event, billableSum, billableCount, feePence)
      : 0;
  const displayTotal = billableSum + recovery;
  const reserved = getAmountReservedOnInvoice(invoice);
  return Math.max(0, Math.round((displayTotal - reserved) * 100) / 100);
}

/**
 * Invoice total (tickets + credit recovery + stored transaction fee) under **current** event rules.
 * Matches {@link syncEventInvoiceTotalsToPricing} maths so dashboards stay aligned when stored
 * `totalAmount` on settled invoices is not rewritten.
 */
export function computeInvoicePolicyDisplayTotal(
  invoice: Invoice,
  event: Event,
  rsvpList: RSVP[],
): number {
  const ordered = orderRsvpsForInvoice(invoice, rsvpList);
  if (ordered.length === 0) {
    return Number(invoice.totalAmount) || 0;
  }
  const payer = getInvoicePayerRsvp(ordered[0], rsvpList);
  const ticket = computeInvoiceBaseAmountForPayer(payer, event, rsvpList);
  const slots = countHostInvoiceCreditRecoverySlots(payer, event, rsvpList);
  const feePence = getCreditBookingFeePenceForEvent(event);
  const recovery =
    ticket > 0 ? computeCreditRecoveryTotalPounds(event, ticket, slots, feePence) : 0;
  const tx =
    typeof invoice.transactionFee === 'number' && Number.isFinite(invoice.transactionFee)
      ? invoice.transactionFee
      : 0;
  return Math.round((ticket + recovery + tx) * 100) / 100;
}

export function ticketSubtotalForBreakdownRow(rsvp: RSVP, event: Event): number {
  return computeRsvpSubtotalForInvoice(rsvp, event);
}
